import { getCookieData } from '@repo/common-utils';
import { useToast } from '@repo/ui';
import { useAuthContext } from '@repo/user-session';
import { useAtomValue } from 'jotai';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useRef, useState } from 'react';

import LoadingContent from './LoadingContent';

import { SearchHometaxRefundResult } from '@/__generated__/DecisionRequestLoadingMutation.graphql';
import { searchHometaxRefundLoadEmployeeIncreaseMutation$data } from '@/__generated__/searchHometaxRefundLoadEmployeeIncreaseMutation.graphql';
import { searchHometaxRefundReloadMutation$data } from '@/__generated__/searchHometaxRefundReloadMutation.graphql';
import { searchHometaxRefundResultMutation$data } from '@/__generated__/searchHometaxRefundResultMutation.graphql';
import { AUTO_LOGIN_CODE_KEY } from '@/components/tax-refund/common/data/auth-info';
import { CHECK_DECISION_ACTION_CODE_COOKIE_KEY, getResultRefundData, setResultRefundData } from '@/components/tax-refund/common/data/result-refund-data';
import NetworkError from '@/components/tax-refund/common/view/ErrorView/NetworkError';
import { 환급결정확인알림톡_PATH } from '@/lib/constants/paths';
import useSaveRefundActionLog from '@/lib/hooks/api/mutation/use-save-refund-action-log';
import useSearchHometaxRefundResult from '@/lib/hooks/api/mutation/use-search-hometax-refund-result';
import { useBizMessageRequestType } from '@/lib/hooks/use-biz-message-request-type';
import useNavigatorOnline from '@/lib/hooks/use-navigator-online';
import { refundTokenAtom } from '@/lib/stores/simple-auth';

const ResultRequestContent = () => {
  const router = useRouter();
  const { toast } = useToast();
  const { requestType, isSetRequestType } = useBizMessageRequestType();
  const { commit, isInFlight } = useSearchHometaxRefundResult(requestType);
  const { commit: saveActionLog } = useSaveRefundActionLog();
  const isStartRequest = useRef(false);
  const refundToken = useAtomValue(refundTokenAtom);
  const {
    auth: { apiToken }
  } = useAuthContext();
  let retryCount = 0;

  const [showNetworkDisconnection, setShowNetworkDisconnection] = useState(false);
  const [disabledButton, setDisabledButton] = useState(false);
  let online = window?.navigator?.onLine ?? true;

  const data = getResultRefundData();

  const setOnline = (_online: boolean) => {
    online = _online;
    setDisabledButton(!_online);
  };
  useNavigatorOnline(setOnline);

  const request = useCallback(() => {
    if (isStartRequest.current || isInFlight || !refundToken || !apiToken) return;

    isStartRequest.current = true;
    commit({
      variables: {
        refundToken: refundToken ?? '',
        code: sessionStorage.getItem(AUTO_LOGIN_CODE_KEY) ?? ''
      },
      onCompleted: (res, errs) => {
        let resultData;
        let hometaxRefundResult: SearchHometaxRefundResult | null | undefined;
        if (requestType === 'checkDecision') {
          resultData = (res as searchHometaxRefundResultMutation$data).searchHometaxRefundResult;
          hometaxRefundResult = resultData?.hometaxRefundResult;
        } else if (requestType === 'recollect') {
          resultData = (res as searchHometaxRefundReloadMutation$data).reload;
        } else {
          resultData = (res as searchHometaxRefundLoadEmployeeIncreaseMutation$data).loadEmployeeIncrease;
        }

        const { result, errors, taxpayerName } = resultData;
        if (errs) {
          console.error(errs);
          toast({ title: '알 수 없는 오류입니다.', variant: 'warning' });
          return;
        }

        setResultRefundData({
          ...data,
          taxpayerName: taxpayerName ?? ''
        });

        // 3초 후에 페이지 이동
        setTimeout(() => {
          let resultPath = 환급결정확인알림톡_PATH.환급결정내역없음;
          const actionCode = router.query.actionCode ?? getCookieData(CHECK_DECISION_ACTION_CODE_COOKIE_KEY);
          if (result) {
            if (requestType === 'recollect' || requestType === 'job_retention' || hometaxRefundResult === 'Accept') {
              resultPath = 환급결정확인알림톡_PATH.환급결정내역있음;
            } else if (hometaxRefundResult === 'Reject') {
              resultPath = 환급결정확인알림톡_PATH.환급불가;
            }
          } else {
            switch (errors?.code) {
              case 'ERR_NO_EQUAL_TIN':
                resultPath = 환급결정확인알림톡_PATH.환급결정로딩에러TIN비일치;
                break;
              case 'ERR_HOMETAX_ETC':
                resultPath = 환급결정확인알림톡_PATH.환급결정로딩에러홈택스;
                break;
              default:
                resultPath = 환급결정확인알림톡_PATH.환급결정로딩에러기타;
                break;
            }
          }
          isStartRequest.current = false;

          if (actionCode) {
            saveActionLog({
              variables: {
                actionCode,
                actionStatus: errors?.code ? '조회에러' : '조회완료'
              }
            });
          }

          router.replace(resultPath);
        }, 3000);
      },
      onError: (err) => {
        console.error(err);
        if (!online) {
          setShowNetworkDisconnection(true);
          return;
        }

        if (retryCount >= 5) {
          router.replace(환급결정확인알림톡_PATH.환급결정로딩에러기타);
          return;
        }

        toast({ title: '네트워크 불안정으로 다시 확인할게요.', variant: 'warning' });
        setTimeout(() => {
          isStartRequest.current = false;
          retryCount += 1;
          request();
        }, 1000);
      }
    });
  }, [commit, isInFlight, refundToken, apiToken]);

  useEffect(() => {
    if (isSetRequestType) request();
  }, [request, isSetRequestType]);

  const handleRetry = () => {
    setShowNetworkDisconnection(false);
    request();
  };

  return <>{showNetworkDisconnection ? <NetworkError disabledPrimaryButton={disabledButton} onPrimaryButtonClick={handleRetry} /> : <LoadingContent />}</>;
};

export default ResultRequestContent;
